import React, { useState } from 'react';
import { useMessageGetter } from 'react-message-context';
import { RouteComponentProps } from 'react-router-dom';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { loginUser } from '../../context/kennoTenantActions';
import UnauthenticatedPageContainer from './UnauthenticatedPageContainer';
import { Routes } from '../../constants/routes';
import Loader from '../../components/basic/Loader';
import { SignInFormState, signInSchema } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDate, scrollToTop } from '../../utils';
import LoginForm from './LoginForm';
import SignInOneTimePassword from './SignInOneTimePassword';
import { requestConfirmationCode } from '../../apis_kenno';
import { UNAUTHORIZED_ERROR_TYPE } from '../../types/exceptions';

const SignInPage: React.FC<RouteComponentProps> = ({ history, location }) => {
  const t = useMessageGetter('signin');
  const [{ user }, kennoTenantDispatch] = useKennoTenant();
  const [isBusy, setIsBusy] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [otpRequired, setOtpRequired] = useState<boolean>(false);

  const formMethods = useForm<SignInFormState>({ resolver: zodResolver(signInSchema) });

  /**
   * Redirects the user back to the page they were trying to view.
   * If the page was the signin page, redirect to home.
   */
  const navigateToSite = () => {
    // `from` is set by AuthenticatedRoute when redirecting here
    const from: string | undefined = (location.state as any)?.from;
    if (!from || from === Routes.SIGN_IN) {
      history.push(Routes.HOME);
    } else {
      history.push(from);
    }
  };

  if (user.isLoggedIn) {
    navigateToSite();
  }

  const handleLoginErrorTypes = async (error: any) => {
    if (error.response?.status === 401) {
      switch (error.response?.data?.errorType) {
        case UNAUTHORIZED_ERROR_TYPE.INCORRECT_CREDENTIALS:
          setErrorMessage(t('error.incorrectCredentials'));
          break;
        case UNAUTHORIZED_ERROR_TYPE.CONTRACT_NOT_STARTED:
          setErrorMessage(
            t('error.contractNotStarted', {
              startDate: formatDate(error.response.data.startDate),
            })
          );
          break;
        case UNAUTHORIZED_ERROR_TYPE.CONTRACT_EXPIRED:
          setErrorMessage(t('error.contractExpired'));
          break;
        case UNAUTHORIZED_ERROR_TYPE.CONTRACT_NOT_FOUND:
          setErrorMessage(t('error.contractNotFound'));
          break;
        case UNAUTHORIZED_ERROR_TYPE.CONFIRMATION_CODE_REQUIRED:
          // In case user did not finish registration, they must input their OTP now
          // Show the form and send them a new OTP
          const username = formMethods.getValues('username');
          try {
            await requestConfirmationCode(username);
            setErrorMessage('');
            setOtpRequired(true);
          } catch (e) {
            setErrorMessage(t('error.general'));
          }
          break;
        default:
          setErrorMessage(t('error.general'));
          break;
      }
    } else {
      setErrorMessage(t('error.general'));
    }
    setLoginFailed(true);
    setIsBusy(false);
  };

  const onSubmit: SubmitHandler<SignInFormState> = async (data) => {
    scrollToTop();
    setLoginFailed(false);
    setIsBusy(true);
    try {
      await loginUser(kennoTenantDispatch, data);
      navigateToSite();
    } catch (error: any) {
      handleLoginErrorTypes(error);
    }
  };

  if (isBusy || !user.isLoaded) {
    return (
      <UnauthenticatedPageContainer>
        <Loader />
      </UnauthenticatedPageContainer>
    );
  }

  const onCancelOtpStep = () => {
    setOtpRequired(false);
    setLoginFailed(false);
    formMethods.reset();
  };

  if (otpRequired) {
    return (
      <UnauthenticatedPageContainer>
        <SignInOneTimePassword
          username={formMethods.getValues('username')}
          onSuccess={formMethods.handleSubmit(onSubmit)}
          onCancel={onCancelOtpStep}
        />
      </UnauthenticatedPageContainer>
    );
  }

  return (
    <UnauthenticatedPageContainer>
      <FormProvider {...formMethods}>
        <LoginForm
          onSubmit={formMethods.handleSubmit(onSubmit)}
          loginFailed={loginFailed}
          errorMessage={errorMessage}
        />
      </FormProvider>
    </UnauthenticatedPageContainer>
  );
};

export default SignInPage;
